import React, { useState, useCallback, useMemo } from "react";
import { toast } from "react-hot-toast";
import userServices from "../../api/services/userService";
import { confirmDanger } from "../../utils/confirmDialog";
import {
  FilterSection,
  ActiveFilterPills,
  UserProfilesTable,
  UserProfilesPagination,
  EditUserProfileModal,
  CreateAppUserModal,
  useUserProfiles,
} from "../../components/UserProfiles";

const PROFILE_URL = "https://naseebi.com/en/home/profiledetail";

export default function UserProfiles() {
  const {
    filters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    secondaryFields,
    users,
    total,
    isLoading,
    backendPage,
    setBackendPage,
    backendLimit,
    fetchFilteredUsers,
  } = useUserProfiles();

  const [editUserId, setEditUserId] = useState(null);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const adminRoleKey = useMemo(() => {
    const role =
      (typeof window !== "undefined" && localStorage.getItem("adminUserRole")) || "";
    return String(role).trim().toLowerCase();
  }, []);

  /** Create app user: Admin + Developer only (CSR uses edit only). */
  const canCreateAppUser = useMemo(
    () => adminRoleKey === "admin" || adminRoleKey === "developer",
    [adminRoleKey]
  );

  /** Edit app user profile: Admin, Developer, CSR. */
  const canEditUserProfile = useMemo(
    () =>
      adminRoleKey === "admin" ||
      adminRoleKey === "developer" ||
      adminRoleKey === "csr",
    [adminRoleKey]
  );

  const canDeleteAppUser = useMemo(() => {
    const role = (typeof window !== "undefined" && localStorage.getItem("adminUserRole")) || "";
    return String(role).trim().toLowerCase() === "developer" || String(role).trim().toLowerCase() === "admin";
  }, []);

  const handleApply = () => {
    setBackendPage(1);
  };

  const handleViewProfile = (id) => {
    window.open(`${PROFILE_URL}/${id}`, "_blank");
  };

  const handleEditUser = useCallback((id) => {
    setEditUserId(id);
  }, []);

  const handleEditClose = useCallback(() => setEditUserId(null), []);

  const handleUserSaved = useCallback(() => {
    return fetchFilteredUsers(filters, backendPage, { refresh: true });
  }, [fetchFilteredUsers, filters, backendPage]);

  const handleDeleteUser = useCallback(
    async (userRow) => {
      const id = userRow?.id;
      if (!id) return;
      const name = `${userRow.firstName || ""} ${userRow.lastName || ""}`.trim() || `User #${id}`;
      const ok = await confirmDanger(
        `Permanently delete ${name} (ID ${id})? This removes the account and related data and cannot be undone.`,
        "Delete app user?"
      );
      if (!ok) return;
      setDeletingId(id);
      try {
        const res = await userServices.deleteAppUserByDeveloper(id);
        if (res?.success) {
          toast.success(res?.message || "User deleted");
          fetchFilteredUsers(filters, backendPage, { refresh: true });
        } else {
          toast.error(res?.message || "Delete failed");
        }
      } catch (err) {
        const msg =
          (err && typeof err === "object" && err.message) ||
          (typeof err === "string" ? err : null) ||
          "Failed to delete user";
        toast.error(msg);
      } finally {
        setDeletingId(null);
      }
    },
    [fetchFilteredUsers, filters, backendPage]
  );

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-6">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
          <h3 className="text-md md:text-xl font-bold text-gray-800">
            User Profiles
          </h3>
          <div className="flex items-center gap-2">
            {canCreateAppUser && (
              <button
                type="button"
                onClick={() => setCreateUserOpen(true)}
                className="text-xs md:text-sm font-semibold px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600">
                Create user
              </button>
            )}
            {getActiveFiltersCount() > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs md:text-sm text-red-600 hover:text-red-700 font-medium">
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        <ActiveFilterPills
          activeKeys={activeFilterEntries}
          filters={filters}
          getLabel={getFilterLabel}
          onRemove={removeFilter}
        />

        <FilterSection
          primaryFields={primaryFields}
          secondaryFields={secondaryFields}
          values={filters}
          onChange={handleFilterChange}
          onApply={handleApply}
          onClear={clearFilters}
          applyLabel="Apply"
          clearLabel="Clear All"
        />

        <UserProfilesTable
          users={users}
          page={backendPage}
          limit={backendLimit}
          isLoading={isLoading}
          activeFiltersCount={getActiveFiltersCount()}
          onViewProfile={handleViewProfile}
          onEditUser={canEditUserProfile ? handleEditUser : undefined}
          onDeleteUser={canDeleteAppUser ? handleDeleteUser : undefined}
          deletingUserId={deletingId}
        />

        <EditUserProfileModal
          userId={editUserId}
          isOpen={editUserId != null}
          onClose={handleEditClose}
          onSaved={handleUserSaved}
        />

        <CreateAppUserModal
          isOpen={createUserOpen}
          onClose={() => setCreateUserOpen(false)}
          onCreated={() => {
            setBackendPage(1);
            return fetchFilteredUsers(filters, 1, { refresh: true });
          }}
        />

        {total > 0 && (
          <UserProfilesPagination
            page={backendPage}
            total={total}
            limit={backendLimit}
            onPageChange={setBackendPage}
          />
        )}
      </div>
    </div>
  );
}
